import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Tag,
  Tabs,
  Typography,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  CheckOutlined,
  CloseOutlined,
  CarOutlined,
  ClearOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

const { Title } = Typography;

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
}

interface City {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  regionId: number;
  region?: Region;
}

interface BusStation {
  id: number;
  cityId: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  address?: string;
  lat?: number;
  lng?: number;
  phone?: string;
  isActive: boolean;
  city?: City;
}

interface BusLayout {
  id: number;
  name: string;
  totalSeats: number;
  rows: number;
  seatsPerRow: number;
  leftSeats: number;
  rightSeats: number;
  backRowSeats: number;
  hasBackRow: boolean;
  seatConfig?: number[][]; // Array of [row, col] positions
  description?: string;
  isActive: boolean;
  _count?: { busTrips: number };
}

// Grid-based Seat Editor Component
const SeatGridEditor = ({
  selectedSeats,
  onSeatsChange,
  maxRows = 15,
  maxCols = 6,
}: {
  selectedSeats: number[][]; // Array of [row, col] positions
  onSeatsChange: (seats: number[][]) => void;
  maxRows?: number;
  maxCols?: number;
}) => {
  const cellSize = 36;
  const gap = 4;
  const aisleAfterCol = 2; // Aisle after column 2 (0-indexed: after cols 0,1,2)

  const toggleSeat = useCallback((row: number, col: number) => {
    const existingIndex = selectedSeats.findIndex(
      ([r, c]) => r === row && c === col
    );
    if (existingIndex >= 0) {
      // Remove seat
      const newSeats = [...selectedSeats];
      newSeats.splice(existingIndex, 1);
      onSeatsChange(newSeats);
    } else {
      // Add seat
      onSeatsChange([...selectedSeats, [row, col]]);
    }
  }, [selectedSeats, onSeatsChange]);

  const isSeatSelected = useCallback((row: number, col: number) => {
    return selectedSeats.some(([r, c]) => r === row && c === col);
  }, [selectedSeats]);

  const getSeatNumber = useCallback((row: number, col: number) => {
    // Sort seats by row then by column to get seat number
    const sortedSeats = [...selectedSeats].sort((a, b) => {
      if (a[0] !== b[0]) return a[0] - b[0];
      return a[1] - b[1];
    });
    const index = sortedSeats.findIndex(([r, c]) => r === row && c === col);
    return index >= 0 ? index + 1 : null;
  }, [selectedSeats]);

  const totalWidth = maxCols * (cellSize + gap) + 20; // 20 for aisle
  const totalHeight = maxRows * (cellSize + gap) + 60; // 60 for driver area

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          width: totalWidth,
          minHeight: totalHeight,
          background: '#f0f0f0',
          borderRadius: 12,
          padding: 12,
          position: 'relative',
        }}
      >
        {/* Driver area */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 12,
            width: cellSize,
            height: cellSize,
            background: '#1a1a2e',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CarOutlined style={{ color: '#fff', fontSize: 16 }} />
        </div>

        {/* Grid */}
        <div style={{ marginTop: cellSize + gap + 8 }}>
          {Array.from({ length: maxRows }).map((_, rowIndex) => (
            <div key={rowIndex} style={{ display: 'flex', marginBottom: gap }}>
              {Array.from({ length: maxCols }).map((_, colIndex) => {
                const isSelected = isSeatSelected(rowIndex, colIndex);
                const seatNumber = getSeatNumber(rowIndex, colIndex);
                const isAfterAisle = colIndex > aisleAfterCol;

                return (
                  <Tooltip
                    key={colIndex}
                    title={isSelected ? `O'rindiq ${seatNumber} - O'chirish uchun bosing` : "O'rindiq qo'shish"}
                  >
                    <div
                      onClick={() => toggleSeat(rowIndex, colIndex)}
                      style={{
                        width: cellSize,
                        height: cellSize,
                        marginLeft: colIndex === 0 ? 0 : (isAfterAisle && colIndex === aisleAfterCol + 1 ? gap + 20 : gap),
                        background: isSelected ? '#00C4AD' : '#e8e8e8',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: isSelected ? '#fff' : '#999',
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: 11,
                        border: isSelected ? '2px solid #00a896' : '2px dashed #ccc',
                        transition: 'all 0.2s',
                        userSelect: 'none',
                      }}
                    >
                      {isSelected ? seatNumber : ''}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend and stats */}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 20,
              height: 20,
              background: '#00C4AD',
              borderRadius: 4,
              border: '2px solid #00a896',
            }} />
            <span style={{ fontSize: 12 }}>Tanlangan</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 20,
              height: 20,
              background: '#e8e8e8',
              borderRadius: 4,
              border: '2px dashed #ccc',
            }} />
            <span style={{ fontSize: 12 }}>Bo'sh joy</span>
          </div>
        </div>
        <Tag color="blue" style={{ fontSize: 14 }}>
          Jami: {selectedSeats.length} ta o'rindiq
        </Tag>
      </div>
    </div>
  );
};

// Bus Diagram Preview Component (read-only)
const BusDiagramPreview = ({
  seatConfig,
  compact = false,
}: {
  seatConfig: number[][];
  compact?: boolean;
}) => {
  const cellSize = compact ? 24 : 32;
  const gap = compact ? 3 : 4;

  if (!seatConfig || seatConfig.length === 0) {
    return <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>O'rindiqlar yo'q</div>;
  }

  // Sort seats to get seat numbers
  const sortedSeats = [...seatConfig].sort((a, b) => {
    if (a[0] !== b[0]) return a[0] - b[0];
    return a[1] - b[1];
  });

  // Find grid dimensions
  const maxRow = Math.max(...seatConfig.map(([r]) => r));
  const maxCol = Math.max(...seatConfig.map(([, c]) => c));
  const minCol = Math.min(...seatConfig.map(([, c]) => c));

  const totalWidth = (maxCol - minCol + 1) * (cellSize + gap) + 30; // 30 for aisle
  const totalHeight = (maxRow + 1) * (cellSize + gap) + 50;

  const getSeatNumber = (row: number, col: number) => {
    const index = sortedSeats.findIndex(([r, c]) => r === row && c === col);
    return index >= 0 ? index + 1 : null;
  };

  return (
    <div
      style={{
        width: totalWidth,
        height: totalHeight,
        background: '#f5f5f5',
        borderRadius: 8,
        padding: 8,
        position: 'relative',
      }}
    >
      {/* Driver */}
      <div
        style={{
          position: 'absolute',
          top: 6,
          right: 8,
          width: cellSize - 4,
          height: cellSize - 4,
          background: '#1a1a2e',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CarOutlined style={{ color: '#fff', fontSize: compact ? 10 : 12 }} />
      </div>

      {/* Seats */}
      <div style={{ marginTop: cellSize + 4 }}>
        {seatConfig.map(([row, col], idx) => {
          const seatNumber = getSeatNumber(row, col);
          const isAfterAisle = col > 2;
          const left = (col - minCol) * (cellSize + gap) + (isAfterAisle ? 20 : 0);
          const top = row * (cellSize + gap);

          return (
            <div
              key={idx}
              style={{
                position: 'absolute',
                left: left + 8,
                top: top + cellSize + 12,
                width: cellSize,
                height: cellSize,
                background: '#00C4AD',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 600,
                fontSize: compact ? 9 : 11,
              }}
            >
              {seatNumber}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const BusSettings = () => {
  // Stations state
  const [stations, setStations] = useState<BusStation[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [stationsLoading, setStationsLoading] = useState(true);
  const [stationModalOpen, setStationModalOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<BusStation | null>(null);
  const [stationForm] = Form.useForm();

  // Layouts state
  const [layouts, setLayouts] = useState<BusLayout[]>([]);
  const [layoutsLoading, setLayoutsLoading] = useState(true);
  const [layoutModalOpen, setLayoutModalOpen] = useState(false);
  const [editingLayout, setEditingLayout] = useState<BusLayout | null>(null);
  const [layoutForm] = Form.useForm();
  const [selectedSeats, setSelectedSeats] = useState<number[][]>([]);

  // Active tab
  const [activeTab, setActiveTab] = useState('stations');

  // Fetch data on mount
  useEffect(() => {
    fetchStations();
    fetchLayouts();
  }, []);

  // ============ STATIONS METHODS ============

  const fetchStations = async () => {
    setStationsLoading(true);
    try {
      const [stationsRes, regionsRes] = await Promise.all([
        apiClient.get('/admin/bus/stations'),
        apiClient.get('/admin/regions'),
      ]);
      setStations(stationsRes.data);
      setRegions(regionsRes.data);
    } catch (error) {
      message.error('Ma\'lumotlarni yuklashda xatolik');
    } finally {
      setStationsLoading(false);
    }
  };

  const handleRegionChange = async (regionId: number) => {
    stationForm.setFieldValue('cityId', undefined);
    if (regionId) {
      try {
        const response = await apiClient.get(`/admin/cities?regionId=${regionId}`);
        setFilteredCities(response.data);
      } catch (error) {
        message.error('Shaharlarni yuklashda xatolik');
      }
    } else {
      setFilteredCities([]);
    }
  };

  const handleCreateStation = () => {
    setEditingStation(null);
    setFilteredCities([]);
    stationForm.resetFields();
    setStationModalOpen(true);
  };

  const handleEditStation = async (station: BusStation) => {
    setEditingStation(station);
    if (station.city?.region?.id) {
      try {
        const response = await apiClient.get(`/admin/cities?regionId=${station.city.region.id}`);
        setFilteredCities(response.data);
      } catch (error) {
        console.error('Failed to load cities:', error);
      }
    }
    stationForm.setFieldsValue({
      regionId: station.city?.region?.id,
      cityId: station.cityId,
      nameUz: station.nameUz,
      nameRu: station.nameRu,
      nameEn: station.nameEn,
      address: station.address,
      lat: station.lat,
      lng: station.lng,
      phone: station.phone,
    });
    setStationModalOpen(true);
  };

  const handleDeleteStation = async (id: number) => {
    try {
      await apiClient.delete(`/admin/bus/stations/${id}`);
      message.success('Avtovokzal o\'chirildi');
      fetchStations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'O\'chirishda xatolik');
    }
  };

  const handleToggleStation = async (id: number) => {
    try {
      await apiClient.put(`/admin/bus/stations/${id}/toggle`);
      message.success('Holat o\'zgartirildi');
      fetchStations();
    } catch (error) {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmitStation = async (values: any) => {
    try {
      const data = {
        cityId: values.cityId,
        nameUz: values.nameUz,
        nameRu: values.nameRu,
        nameEn: values.nameEn,
        address: values.address,
        lat: values.lat,
        lng: values.lng,
        phone: values.phone,
      };

      if (editingStation) {
        await apiClient.put(`/admin/bus/stations/${editingStation.id}`, data);
        message.success('Avtovokzal yangilandi');
      } else {
        await apiClient.post('/admin/bus/stations', data);
        message.success('Avtovokzal yaratildi');
      }
      setStationModalOpen(false);
      fetchStations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    }
  };

  // ============ LAYOUTS METHODS ============

  const fetchLayouts = async () => {
    setLayoutsLoading(true);
    try {
      const response = await apiClient.get('/admin/bus/layouts');
      setLayouts(response.data);
    } catch (error) {
      message.error('Sxemalarni yuklashda xatolik');
    } finally {
      setLayoutsLoading(false);
    }
  };

  const handleCreateLayout = () => {
    setEditingLayout(null);
    layoutForm.resetFields();
    setSelectedSeats([]);
    setLayoutModalOpen(true);
  };

  const handleEditLayout = (layout: BusLayout) => {
    setEditingLayout(layout);
    layoutForm.setFieldsValue({
      name: layout.name,
      description: layout.description,
    });
    // Load seat config
    if (layout.seatConfig && Array.isArray(layout.seatConfig)) {
      setSelectedSeats(layout.seatConfig);
    } else {
      // Generate default seat config from old format
      const seats: number[][] = [];
      let seatNum = 0;
      const regularSeats = layout.hasBackRow ? layout.totalSeats - layout.backRowSeats : layout.totalSeats;
      const seatsPerRow = layout.leftSeats + layout.rightSeats;
      const numRows = Math.ceil(regularSeats / seatsPerRow);

      for (let row = 0; row < numRows && seatNum < regularSeats; row++) {
        for (let col = 0; col < layout.leftSeats && seatNum < regularSeats; col++) {
          seats.push([row, col]);
          seatNum++;
        }
        for (let col = 0; col < layout.rightSeats && seatNum < regularSeats; col++) {
          seats.push([row, 3 + col]); // 3 = aisleAfterCol + 1
          seatNum++;
        }
      }
      if (layout.hasBackRow) {
        const backRow = numRows;
        for (let col = 0; col < layout.backRowSeats; col++) {
          seats.push([backRow, col]);
        }
      }
      setSelectedSeats(seats);
    }
    setLayoutModalOpen(true);
  };

  const handleDeleteLayout = async (id: number) => {
    try {
      await apiClient.delete(`/admin/bus/layouts/${id}`);
      message.success('Sxema o\'chirildi');
      fetchLayouts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'O\'chirishda xatolik');
    }
  };

  const [seedingLayouts, setSeedingLayouts] = useState(false);
  const [seedingDemoTrips, setSeedingDemoTrips] = useState(false);

  const handleSeedDemoTrips = async () => {
    setSeedingDemoTrips(true);
    try {
      const res = await apiClient.post<{
        results: { direction: string; departureTime: string; action: 'created' | 'skipped' }[];
      }>('/admin/bus/demo-trips/seed');
      const results = res.data?.results ?? [];
      const created = results.filter((r) => r.action === 'created').length;
      const skipped = results.filter((r) => r.action === 'skipped').length;
      if (created > 0) {
        message.success(
          `${created} ta demo reys qo'shildi${
            skipped > 0 ? `, ${skipped} ta allaqachon mavjud edi` : ''
          }`,
        );
      } else {
        message.info("Barcha demo reyslar allaqachon mavjud edi");
      }
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Demo reyslarni yuklashda xatolik",
      );
    } finally {
      setSeedingDemoTrips(false);
    }
  };

  const handleSeedLayouts = async () => {
    setSeedingLayouts(true);
    try {
      const res = await apiClient.post<{
        results: { name: string; action: 'created' | 'skipped' }[];
      }>('/admin/bus/layouts/seed');
      const results = res.data?.results ?? [];
      const created = results.filter((r) => r.action === 'created').length;
      const skipped = results.filter((r) => r.action === 'skipped').length;
      if (created > 0) {
        message.success(
          `${created} ta yangi sxema qo'shildi${
            skipped > 0 ? `, ${skipped} ta allaqachon mavjud edi` : ''
          }`,
        );
      } else {
        message.info("Barcha mashhur sxemalar allaqachon mavjud edi");
      }
      await fetchLayouts();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Sxemalarni yuklashda xatolik",
      );
    } finally {
      setSeedingLayouts(false);
    }
  };

  const handleToggleLayout = async (id: number) => {
    try {
      await apiClient.put(`/admin/bus/layouts/${id}/toggle`);
      message.success('Holat o\'zgartirildi');
      fetchLayouts();
    } catch (error) {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmitLayout = async (values: any) => {
    if (selectedSeats.length < 5) {
      message.error('Kamida 5 ta o\'rindiq tanlang');
      return;
    }

    try {
      // Calculate layout parameters from selected seats
      const rows = Math.max(...selectedSeats.map(([r]) => r)) + 1;
      const leftSeats = selectedSeats.filter(([, c]) => c <= 2).length > 0
        ? Math.max(...selectedSeats.filter(([, c]) => c <= 2).map(([, c]) => c)) + 1
        : 0;
      const rightSeats = selectedSeats.filter(([, c]) => c > 2).length > 0
        ? 6 - Math.min(...selectedSeats.filter(([, c]) => c > 2).map(([, c]) => c))
        : 0;

      const data = {
        name: values.name,
        totalSeats: selectedSeats.length,
        rows,
        seatsPerRow: 4,
        leftSeats: leftSeats || 2,
        rightSeats: rightSeats || 2,
        backRowSeats: 5,
        hasBackRow: false,
        seatConfig: selectedSeats,
        description: values.description,
      };

      if (editingLayout) {
        await apiClient.put(`/admin/bus/layouts/${editingLayout.id}`, data);
        message.success('Sxema yangilandi');
      } else {
        await apiClient.post('/admin/bus/layouts', data);
        message.success('Sxema yaratildi');
      }
      setLayoutModalOpen(false);
      fetchLayouts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    }
  };

  const handleClearSeats = () => {
    setSelectedSeats([]);
  };

  // ============ TABLE COLUMNS ============

  const stationColumns = [
    {
      title: 'Nomi',
      key: 'name',
      render: (_: any, record: BusStation) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.nameUz}</div>
          <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.nameRu}</div>
        </div>
      ),
    },
    {
      title: 'Shahar',
      key: 'city',
      render: (_: any, record: BusStation) => (
        <div>
          <div>{record.city?.nameUz}</div>
          <div style={{ fontSize: 12, color: '#7F8C8D' }}>
            {record.city?.region?.nameUz}
          </div>
        </div>
      ),
    },
    {
      title: 'Manzil',
      dataIndex: 'address',
      key: 'address',
      render: (address: string) => address || '-',
    },
    {
      title: 'Koordinatalar',
      key: 'coordinates',
      render: (_: any, record: BusStation) =>
        record.lat && record.lng ? (
          <span style={{ fontSize: 12 }}>
            {Number(record.lat).toFixed(6)}, {Number(record.lng).toFixed(6)}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Faol' : 'Nofaol'}
        </Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: any, record: BusStation) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditStation(record)}
          />
          <Popconfirm
            title={record.isActive ? 'O\'chirishni tasdiqlaysizmi?' : 'Yoqishni tasdiqlaysizmi?'}
            onConfirm={() => handleToggleStation(record.id)}
          >
            <Button
              type="text"
              icon={record.isActive ? <CloseOutlined /> : <CheckOutlined />}
            />
          </Popconfirm>
          <Popconfirm
            title="O'chirishni tasdiqlaysizmi?"
            description="Bu avtovokzal butunlay o'chiriladi"
            onConfirm={() => handleDeleteStation(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const layoutColumns = [
    {
      title: 'Nomi',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: BusLayout) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "O'rindiqlar",
      dataIndex: 'totalSeats',
      key: 'totalSeats',
      render: (totalSeats: number) => (
        <Tag color="blue">{totalSeats} ta</Tag>
      ),
    },
    {
      title: 'Sxema',
      key: 'diagram',
      width: 200,
      render: (_: any, record: BusLayout) => (
        <div style={{ padding: 4 }}>
          {record.seatConfig ? (
            <BusDiagramPreview seatConfig={record.seatConfig} compact />
          ) : (
            <span style={{ color: '#999' }}>-</span>
          )}
        </div>
      ),
    },
    {
      title: 'Reyslar',
      key: 'trips',
      render: (_: any, record: BusLayout) => (
        <Tag>{record._count?.busTrips || 0} ta reys</Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Faol' : 'Nofaol'}
        </Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: any, record: BusLayout) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditLayout(record)}
          />
          <Popconfirm
            title={record.isActive ? 'O\'chirishni tasdiqlaysizmi?' : 'Yoqishni tasdiqlaysizmi?'}
            onConfirm={() => handleToggleLayout(record.id)}
          >
            <Button
              type="text"
              icon={record.isActive ? <CloseOutlined /> : <CheckOutlined />}
            />
          </Popconfirm>
          <Popconfirm
            title="O'chirishni tasdiqlaysizmi?"
            description="Bu sxema butunlay o'chiriladi"
            onConfirm={() => handleDeleteLayout(record.id)}
            disabled={(record._count?.busTrips || 0) > 0}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={(record._count?.busTrips || 0) > 0}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ============ TAB ITEMS ============

  const tabItems = [
    {
      key: 'stations',
      label: (
        <span>
          <EnvironmentOutlined />
          Avtovokzallar
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Title level={5} style={{ margin: 0 }}>
              Avtovokzallar ro'yxati
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateStation}>
              Yangi avtovokzal
            </Button>
          </div>
          <Table
            dataSource={stations}
            columns={stationColumns}
            rowKey="id"
            loading={stationsLoading}
            pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => `Jami: ${total}` }}
          />
        </Card>
      ),
    },
    {
      key: 'layouts',
      label: (
        <span>
          <CarOutlined />
          Avtobus sxemalari
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={5} style={{ margin: 0 }}>
              Avtobus sxemalari ro'yxati
            </Title>
            <Space>
              <Popconfirm
                title="Demo reyslarni yuklash"
                description="QA uchun 5 kunlik Samarqand ↔ Toshkent demo reyslari yaratiladi (kuniga ertalab/kechki 2 ta, har ikki yo'nalish). Mavjudlari o'tkazib yuboriladi."
                okText="Yuklash"
                cancelText="Bekor qilish"
                onConfirm={handleSeedDemoTrips}
              >
                <Button
                  icon={<CloudDownloadOutlined />}
                  loading={seedingDemoTrips}
                >
                  Demo reyslarni yuklash (QA)
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Mashhur sxemalarni yuklash"
                description="Sprinter 18, Higer 35, Setra 49, Yutong 45, King Long 30, Isuzu 21 sxemalari qo'shiladi. Mavjudlari o'tkazib yuboriladi."
                okText="Yuklash"
                cancelText="Bekor qilish"
                onConfirm={handleSeedLayouts}
              >
                <Button
                  icon={<CloudDownloadOutlined />}
                  loading={seedingLayouts}
                >
                  Mashhur sxemalarni yuklash
                </Button>
              </Popconfirm>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateLayout}>
                Yangi sxema
              </Button>
            </Space>
          </div>
          <Table
            dataSource={layouts}
            columns={layoutColumns}
            rowKey="id"
            loading={layoutsLoading}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>Avtobus sozlamalari</Title>
      <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />

      {/* Station Create/Edit Modal */}
      <Modal
        title={editingStation ? 'Avtovokzalni tahrirlash' : 'Yangi avtovokzal'}
        open={stationModalOpen}
        onCancel={() => setStationModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form form={stationForm} layout="vertical" onFinish={handleSubmitStation}>
          <Form.Item
            label="Viloyat"
            name="regionId"
            rules={[{ required: true, message: 'Viloyatni tanlang' }]}
          >
            <Select
              placeholder="Viloyatni tanlang"
              showSearch
              onChange={handleRegionChange}
              options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Shahar"
            name="cityId"
            rules={[{ required: true, message: 'Shaharni tanlang' }]}
          >
            <Select
              placeholder={filteredCities.length === 0 ? 'Avval viloyatni tanlang' : 'Shaharni tanlang'}
              showSearch
              disabled={filteredCities.length === 0}
              options={filteredCities.map((c) => ({ value: c.id, label: c.nameUz }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Nomi (O'zbekcha)"
            name="nameUz"
            rules={[{ required: true, message: 'Nomini kiriting' }]}
          >
            <Input placeholder="Markaziy avtovokzal" />
          </Form.Item>

          <Form.Item
            label="Nomi (Ruscha)"
            name="nameRu"
            rules={[{ required: true, message: 'Nomini kiriting' }]}
          >
            <Input placeholder="Центральный автовокзал" />
          </Form.Item>

          <Form.Item
            label="Nomi (Inglizcha)"
            name="nameEn"
            rules={[{ required: true, message: 'Nomini kiriting' }]}
          >
            <Input placeholder="Central Bus Station" />
          </Form.Item>

          <Form.Item label="Manzil" name="address">
            <Input placeholder="Amir Temur ko'chasi, 1-uy" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Kenglik (Latitude)"
                name="lat"
                rules={[
                  { type: 'number', min: -90, max: 90, message: 'Kenglik -90 dan 90 gacha bo\'lishi kerak' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="41.311081"
                  step={0.000001}
                  precision={8}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Uzunlik (Longitude)"
                name="lng"
                rules={[
                  { type: 'number', min: -180, max: 180, message: 'Uzunlik -180 dan 180 gacha bo\'lishi kerak' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="69.279737"
                  step={0.000001}
                  precision={8}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Telefon" name="phone">
            <Input placeholder="+998 71 123 45 67" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setStationModalOpen(false)}>Bekor qilish</Button>
              <Button type="primary" htmlType="submit">
                {editingStation ? 'Saqlash' : 'Yaratish'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Layout Create/Edit Modal with Grid Editor */}
      <Modal
        title={editingLayout ? 'Sxemani tahrirlash' : 'Yangi avtobus sxemasi'}
        open={layoutModalOpen}
        onCancel={() => setLayoutModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form form={layoutForm} layout="vertical" onFinish={handleSubmitLayout}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Sxema nomi"
                name="name"
                rules={[{ required: true, message: 'Nomini kiriting' }]}
              >
                <Input placeholder="Mercedes Sprinter 20 o'rindiqli" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Izoh" name="description">
                <Input placeholder="Qo'shimcha ma'lumot..." />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 500 }}>O'rindiqlarni tanlang (ustiga bosing)</span>
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={handleClearSeats}
                disabled={selectedSeats.length === 0}
              >
                Tozalash
              </Button>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              background: '#fafafa',
              padding: 16,
              borderRadius: 8,
              border: '1px solid #f0f0f0',
            }}>
              <SeatGridEditor
                selectedSeats={selectedSeats}
                onSeatsChange={setSelectedSeats}
                maxRows={15}
                maxCols={6}
              />
            </div>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setLayoutModalOpen(false)}>Bekor qilish</Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={selectedSeats.length < 5}
              >
                {editingLayout ? 'Saqlash' : 'Yaratish'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
